import React, { useMemo, useState } from 'react';
import { useAppSelector } from '../../hooks/redux';
import {
  buildTransportRequestPanelRows,
  countTrPanelTabs,
  type TrLiveStatus,
  type TrPanelTab,
} from '../../utils/transportRequestPanel';
import SimulationAccordion from './SimulationAccordion';

const TABS: Array<{ id: TrPanelTab; label: string }> = [
  { id: 'active', label: 'ACTIVE' },
  { id: 'pending', label: 'PENDING' },
  { id: 'completed', label: 'COMPLETED' },
];

function statusClass(status: TrLiveStatus): string {
  switch (status) {
    case 'IN TRANSIT':
      return 'text-emerald-600';
    case 'PICKUP':
      return 'text-amber-600';
    case 'WAITING':
      return 'text-slate-500';
    case 'PENDING':
      return 'text-slate-400';
    case 'COMPLETED':
      return 'text-sky-600';
    default:
      return 'text-slate-500';
  }
}

/**
 * TRANSPORT REQUESTS dashboard panel — Active / Pending / Completed tabs
 * with TR ID, STATUS, OHT, FROM → TO.
 */
const TransportRequestsPanel: React.FC = () => {
  const [tab, setTab] = useState<TrPanelTab>('active');
  const transportRequests = useAppSelector((state) => state.simulation.transportRequests);
  const assignments = useAppSelector((state) => state.simulation.assignments);
  const browserKinetic = useAppSelector((state) => state.simulation.browserKinetic);
  const simulationTime = useAppSelector((state) => state.simulation.simulationTime);

  const rows = useMemo(
    () =>
      buildTransportRequestPanelRows({
        transportRequests,
        assignments,
        browserKinetic,
        simulationTime,
      }),
    [assignments, browserKinetic, simulationTime, transportRequests]
  );

  const counts = useMemo(() => countTrPanelTabs(rows), [rows]);
  const visible = useMemo(() => rows.filter((row) => row.tab === tab), [rows, tab]);

  return (
    <SimulationAccordion
      title="TRANSPORT REQUESTS"
      ariaLabel="Transport requests"
      defaultOpen
      className="flex min-h-0 flex-col"
      bodyClassName="flex h-64 min-h-0 flex-col sm:h-72"
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <div
          className="flex shrink-0 items-end gap-4 border-b border-slate-100 px-3 pt-2"
          role="tablist"
          aria-label="Request filters"
        >
          {TABS.map((item) => {
            const selected = tab === item.id;
            const count = counts[item.id];
            return (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setTab(item.id)}
                className={`relative pb-2 text-[10px] font-semibold tracking-wide transition-colors ${
                  selected ? 'text-sky-600' : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                {item.label} ({count})
                {selected ? (
                  <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-sky-500" />
                ) : null}
              </button>
            );
          })}
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-3 pb-2 pt-1.5 scrollbar-thin">
          {!browserKinetic ? (
            <p className="py-4 text-center text-[10px] text-slate-400">
              Run a simulation to populate transport requests.
            </p>
          ) : visible.length === 0 ? (
            <p className="py-4 text-center text-[10px] text-slate-400">
              No {tab} transport requests.
            </p>
          ) : (
            <table className="w-full table-fixed border-collapse text-left">
              <thead>
                <tr className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">
                  <th className="w-[22%] py-1.5 font-semibold">TR ID</th>
                  <th className="w-[22%] py-1.5 font-semibold">Status</th>
                  <th className="w-[16%] py-1.5 font-semibold">OHT</th>
                  <th className="w-[40%] py-1.5 font-semibold">From → To</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => (
                  <tr
                    key={row.id}
                    className="border-t border-slate-100 text-[10px] leading-tight"
                  >
                    <td className="truncate py-1.5 pr-1 font-medium text-slate-800" title={row.id}>
                      {row.id}
                    </td>
                    <td
                      className={`truncate py-1.5 pr-1 font-semibold uppercase ${statusClass(row.status)}`}
                      title={row.status}
                    >
                      {row.status}
                    </td>
                    <td
                      className="truncate py-1.5 pr-1 font-medium text-slate-700"
                      title={row.assignedOht}
                    >
                      {row.assignedOht}
                    </td>
                    <td className="truncate py-1.5 text-slate-600" title={row.fromTo}>
                      {row.fromTo}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </SimulationAccordion>
  );
};

export default TransportRequestsPanel;
